import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight, ChevronLeft, Search } from 'lucide-react';
import useBoardStore from '../../store/boardStore';

/**
 * Popup para selecionar board de destino em 2 steps: workspace → board.
 * Renderizado via portal no document.body para escapar de stacking contexts da tabela.
 * Z-index 9999 alinhado com Tooltip.jsx (maior valor do projeto).
 */
export default function BoardPickerPopup({ currentBoardId, onSelect, onClose, anchorRef }) {
  const { fetchWorkspaces, fetchAllBoards } = useBoardStore();

  const [step, setStep] = useState('workspaces'); // 'workspaces' | 'boards'
  const [workspaces, setWorkspaces] = useState([]);
  const [allBoards, setAllBoards] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const ref = useRef(null);

  // Calcula posição do popup relativa ao anchor
  useEffect(() => {
    if (!anchorRef?.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setPos({
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
    });
  }, [anchorRef]);

  // Fecha ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (
        ref.current &&
        !ref.current.contains(e.target) &&
        (!anchorRef?.current || !anchorRef.current.contains(e.target))
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose, anchorRef]);

  // Carrega workspaces e todos os boards de uma vez
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([fetchWorkspaces(), fetchAllBoards()])
      .then(([, boards]) => {
        if (cancelled) return;
        // fetchWorkspaces atualiza o store; lemos direto do store via getState
        const wsList = useBoardStore.getState().workspaces || [];
        setWorkspaces(wsList);
        setAllBoards(boards.filter((b) => b.id !== currentBoardId));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [currentBoardId, fetchWorkspaces, fetchAllBoards]);

  const handleSelectWorkspace = useCallback((ws) => {
    setSelectedWorkspace(ws);
    setSearch('');
    setStep('boards');
  }, []);

  const handleBack = useCallback(() => {
    setStep('workspaces');
    setSearch('');
    setSelectedWorkspace(null);
  }, []);

  const boardsForWorkspace = allBoards.filter(
    (b) => b.workspace_id === selectedWorkspace?.id
  );

  const filteredWorkspaces = workspaces.filter((w) =>
    w.name.toLowerCase().includes(search.toLowerCase())
  );

  const filteredBoards = boardsForWorkspace.filter((b) =>
    b.name.toLowerCase().includes(search.toLowerCase())
  );

  const popup = (
    <div
      ref={ref}
      style={{ position: 'absolute', top: pos.top, left: pos.left, zIndex: 9999 }}
      className="bg-[#1e2235] border border-border rounded-xl shadow-2xl w-64 overflow-hidden"
    >
      {/* Header */}
      {step === 'workspaces' ? (
        <div className="px-3 py-2 border-b border-border/50">
          <span className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide">
            Escolher workspace
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border/50">
          <button
            onClick={handleBack}
            className="text-text-secondary hover:text-text-primary transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide truncate">
            {selectedWorkspace?.name}
          </span>
        </div>
      )}

      {/* Busca */}
      <div className="px-3 py-2 border-b border-border/50">
        <div className="flex items-center gap-2 bg-main border border-border rounded-lg px-3 py-1.5">
          <Search className="w-3.5 h-3.5 text-text-secondary shrink-0" />
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={step === 'workspaces' ? 'Pesquisar workspace' : 'Pesquisar quadro'}
            className="flex-1 bg-transparent outline-none text-sm text-text-primary placeholder-text-secondary/50"
          />
        </div>
      </div>

      {/* Lista */}
      <div className="max-h-52 overflow-y-auto">
        {loading ? (
          <div className="py-4 text-center text-xs text-text-secondary">Carregando...</div>
        ) : step === 'workspaces' ? (
          filteredWorkspaces.length === 0 ? (
            <div className="py-4 text-center text-xs text-text-secondary">Nenhuma workspace encontrada</div>
          ) : (
            filteredWorkspaces.map((ws) => (
              <button
                key={ws.id}
                onClick={() => handleSelectWorkspace(ws)}
                className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm text-text-primary hover:bg-white/5 transition-colors"
              >
                <span className="flex items-center gap-2 truncate">
                  {ws.icon && (
                    <span className="text-base shrink-0">{ws.icon}</span>
                  )}
                  <span className="truncate">{ws.name}</span>
                </span>
                <ChevronRight className="w-3.5 h-3.5 text-text-secondary shrink-0" />
              </button>
            ))
          )
        ) : (
          filteredBoards.length === 0 ? (
            <div className="py-4 text-center text-xs text-text-secondary">Nenhum quadro encontrado</div>
          ) : (
            filteredBoards.map((b) => (
              <button
                key={b.id}
                onClick={() => onSelect(b)}
                className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm text-text-primary hover:bg-white/5 transition-colors"
              >
                <span className="truncate">{b.name}</span>
                <ChevronRight className="w-3.5 h-3.5 text-text-secondary shrink-0" />
              </button>
            ))
          )
        )}
      </div>
    </div>
  );

  return createPortal(popup, document.body);
}
